<template>
  <div class="main-div">
    <div class="form-div">
      <VasionInput
        v-model="name"
        title="Name"
        class="input-style"
        inputType="top-white"
        required
        :isInErrorState="formFieldErrors.name"
        @input="val => val ? clearFieldError('name') : null"
      />

      <VasionInput
        v-model="description"
        title="Description"
        class="input-style"
        inputType="top-white"
      />

      <div class="md-layout">
        <div
          v-if="isDynamicGroupCheckboxVisible"
          class="checkbox-div"
        >
          <VasionCheckbox
            id="dynamic-group"
            name="dynamic-group"
            :checked="dynamicGroup"
            :isDisabled="!canEditDynamicGroup"
            @change="toggleDynamicGroup()"
          >
            Dynamic Group
          </VasionCheckbox>
        </div>
        <div class="checkbox-div">
          <VasionCheckbox
            id="active-group"
            name="active-group"
            :checked="active"
            @change="toggleActiveGroup()"
          >
            Active
          </VasionCheckbox>
        </div>
      </div>

      <VasionInput
        v-if="isLDAPGroupInputVisible"
        v-model="ldapGroupName"
        :isDisabled="!canEditLDAPGroup"
        title="LDAP Group"
        class="input-style"
        inputType="top-white"
      />

      <VasionDropList
        v-if="isDepartmentInputVisible"
        v-slot="slotItem"
        v-model="selectedDepartment"
        title="Department"
        :dataArray="departmentList"
        displayName="sName"
        valueName="sName"
        width="100%"
        :type="isAdminUser ? 'add-and-delete': 'plain-list'"
        :showSearchField="false"
        :showAddItem="true"
        addItemText="New Department"
        :forceOpen="forceOpenDepartmentDroplist"
        :overlayList="true"
        :isDisabled="!canEditDepartment"
        @addItemClicked="showAddDepartment = true"
        @deleteClicked="deleteDepartmentClicked"
      >
        {{ slotItem.item.sName }}
      </VasionDropList>
      <div class="bottom-div">
        <VasionButton id="cancel-button" :classProp="'primary-light'" @vasionButtonClicked="cancelClick()">
          CANCEL
        </VasionButton>
        <VasionButton id="ok-button" :classProp="'primary'" @vasionButtonClicked="okClick()">
          OK
        </VasionButton>
      </div>
    </div>

    <VasionSnackbar
      :showSnackbarBool.sync="snackbar.show"
      :snackbarImage="snackbar.image"
      :snackbarSubTitle="snackbar.subtitle"
      :snackbarTitle="snackbar.title"
    />

    <AddDepartmentModal
      :show="showAddDepartment"
      @close="showAddDepartment = false"
      @departmentAdded="departmentAdded"
    />

    <md-dialog id="vasion-confirmation-div" :md-active.sync="showDeleteDepartmentConfirmation" :md-click-outside-to-close="false">
      <VasionConfirmationDialog
        :message="deleteDepartmentConfirmationMessage"
        @noButtonClick="showDeleteDepartmentConfirmation=false"
        @yesButtonClick="deleteDepartment"
      />
    </md-dialog>
  </div>
</template>

<script>
import AddDepartmentModal from '@/components/security/AddDepartmentModal.vue'

export default {
  name: 'ConfigureGroup',
  components: {
    AddDepartmentModal,
  },
  props: {
    modifyGroup: {
      type: Object,
      default: () => {},
      required: false,
    },
  },
  data: function () {
    return {
      active: true,
      currentUser: null,
      description: '',
      dynamicGroup: false,
      forceOpenDepartmentDroplist: false,
      formFieldErrors: {
        name: false,
      },
      ldapGroupName: '',
      name: '',
      selectedDepartment: {
        iID: 0,
        sName: '',
      },
      selectedDepartmentForDelete: null,
      showAddDepartment: false,
      showDeleteDepartmentConfirmation: false,
      snackbar: {
        show: false,
        image: false,
        subtitle: '',
        title: ''
      },
    }
  },
  computed: {
    canConfigureGroups() { return this.$store.getters['common/canConfigureGroups'] },
    canEditDepartment() { return this.canConfigureGroups },
    canEditDynamicGroup() { return this.canConfigureGroups && !this.currentUserIsDepartmentAdmin },
    canEditLDAPGroup() { return this.canConfigureGroups && !this.currentUserIsDepartmentAdmin },
    currentUserIsDepartmentAdmin() { return this.currentUser?.adminForDepartments?.length > 0 && !this.currentUser?.admin },
    deleteDepartmentConfirmationMessage() { return `Are you sure you want to delete this department: '${this.selectedDepartmentForDelete?.sName}'?` },
    departmentList() { return this.$store.state.security.departmentList },
    groupID() { return !this.modifyGroup || !this.modifyGroup.ID ? 0 : this.modifyGroup.ID },
    groupType() {
      return this.$store.state.security.groupType
    },
    headerText() { return this.groupID > 0 ? 'Edit Group' : 'Create Group' },
    isAdminUser() { return this.$store.getters['common/isAdminUser'] },
    isDynamicGroupCheckboxVisible() {
      return !this.isPortalGroup
    },
    isLDAPGroupInputVisible() {
      return !this.isPortalGroup
    },
    isDepartmentInputVisible() {
      return !this.isPortalGroup
    },
    isPortalGroup() {
      return this.groupType === 'portal'
    },
  },
  created: async function () {
    this.currentUser = await this.$store.dispatch('security/getUserDetails', this.$store.state.common.userID)
    await this.$store.dispatch('security/loadDepartmentList')

    this.errorText = ''

    if (this.groupID <= 0) {
      return
    }

    this.active = this.modifyGroup.Active
    this.description = this.modifyGroup.Description
    this.dynamicGroup = this.modifyGroup.Dynamic
    this.ldapGroupName = this.modifyGroup.LDAPName
    this.name = this.modifyGroup.Name

    const foundDepartment = this.departmentList.find((department) => {
      return department.iID === this.modifyGroup.department
    })
    if (foundDepartment) {
      this.selectedDepartment = foundDepartment
    }
  },
  methods: {
    cancelClick() {
      this.$emit('cancel-click')
    },
    clearFieldError(field) {
      this.formFieldErrors[field] = false
    },
    clearFieldErrors() {
      Object.keys(this.formFieldErrors).map(key => this.formFieldErrors[key] = false)
    },
    async deleteDepartment() {
      const department = this.selectedDepartmentForDelete
      const response = await this.$store.dispatch('security/deleteDepartment', { Value: department.iID.toString() })
      if (response?.Value?.length > 0 || response?.ResultStatus === 2) {
        this.snackbar = {
          show: true,
          image: false,
          title: 'Error',
          subtitle: response?.Value ?? `Error deleting department '${department.sName}'`
        }
      }
      else {
        this.snackbar = {
          show: true,
          image: true,
          title: 'Success',
          subtitle: `Department '${department.sName}' deleted`
        }
        if(this.selectedDepartment.iID === department.iID) {
          this.selectedDepartment = {
            iID: 0,
            sName: '',
          }
        }
        await this.$store.dispatch('security/loadDepartmentList')
      }

      this.showDeleteDepartmentConfirmation = false
      this.selectedDepartmentForDelete = null
    },
    deleteDepartmentClicked(department){
      this.selectedDepartmentForDelete = department
      this.showDeleteDepartmentConfirmation = true
    },
    departmentAdded(departmentName){
      const department = this.departmentList.find(x => x.sName === departmentName)
      if (department) {
        this.selectedDepartment = department
      }
      this.forceOpenDepartmentDroplist = true
      this.$nextTick(() => {
        this.forceOpenDepartmentDroplist = false
      })
    },
    async okClick() {
      this.clearFieldErrors()
      let errorText = ''

      if (!this.name) {
        this.formFieldErrors.name = true
      } else if (this.name.trim() === '') {
        errorText += 'Group Name cannot only include spaces.'
      }

      if (Object.values(this.formFieldErrors).some(x => x)) {
        errorText = 'Please fill out all required fields.'
      }

      if (errorText) {
        this.snackbar = {
          show: true,
          image: false,
          title: 'Error',
          subtitle: errorText,
        }
        return
      }

      let savePayload = {
        Name: this.name,
        ID: this.groupID,
        LDAPName: this.ldapGroupName,
        Description: this.description,
        Active: this.active,
        Deleted: !this.active,
        Dynamic: this.dynamicGroup,
        department: this.selectedDepartment.iID,
        isPortalGroup: this.isPortalGroup,
      }

      if (this.isPortalGroup) {
        savePayload.LDAPName = null
        savePayload.Dynamic = false
        savePayload.department = null
      }

      const saveResult = await this.$store.dispatch('security/saveGroupDetails', savePayload)
      if (!saveResult || !saveResult.ID) {
        this.snackbar = {
          show: true,
          image: false,
          title: 'Error',
          subtitle: 'Unable to save the Group.'
        }
        return
      }

      this.$emit('ok-click')
    },
    toggleActiveGroup() {
      this.active = !this.active
    },
    toggleDynamicGroup() {
      this.dynamicGroup = !this.dynamicGroup
    },
  },
}
</script>

<style lang="scss" scoped>
  .main-div {
    width: 480px;
    border: unset;
  }

  .form-div {
    display: flex;
    flex-direction: column;
  }

  .bottom-div {
    text-align: right;
    vertical-align: middle;
    line-height: 50px;
    margin-top: 12px;
  }

  .checkbox-div {
    margin: 12px 0px;
    padding-right: 10px;
  }

  .input-style {
    margin-top: 5px;
    margin-bottom: 5px;
  }
#vasion-confirmation-div {
  z-index: 200 !important;
}
</style>
